import { IonReorder } from "@ionic/react";
import { FormattedMessage } from "react-intl";
import { formatPhoneNumber } from "react-phone-number-input";

import Button from "@components/buttons/Button";
import CircleButton from "@components/buttons/CircleButton";
import CardBase from "@components/cards/CardBase";
import styles from "@components/ClientVisibleTaskResourceCard.module.css";
import PinToggle from "@components/PinToggle";
import ResourceFeatures from "@components/ResourceFeatures";
import Icon from "@components/text/Icon";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import ExternalLink from "@components/typography/ExternalLink";
import useMyExpert from "@hooks/useMyExpert";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import { GetJourneyWithPlanQuery, PinResourceTypeEnum, ResourceFragment, TaskFragment } from "@typing/Generated";

type Props = {
  generateHandleDelete: (resource: ResourceFragment) => () => void;
  generateHandleEdit: (resource: ResourceFragment) => () => void;
  journey: GetJourneyWithPlanQuery["journey"];
  resource: GetJourneyWithPlanQuery["journey"]["goals"][0]["tasks"][0]["resources"][0];
  task: TaskFragment;
  updateUseful: (resource: ResourceFragment, useful: boolean) => void;
};

const ClientVisibleTaskResourceCard = ({
  generateHandleDelete,
  generateHandleEdit,
  journey,
  resource,
  task,
  updateUseful
}: Props) => {
  const myExpert = useMyExpert();
  const sanitizedDescription = useSanitizeHtml({ journeyId: journey.id, rawHtml: resource.description, resource });

  return (
    <CardBase
      body={
        <div className="flex flex-column justify-between full-height">
          <div>
            <div className="flex justify-between ion-margin-bottom">
              <BodyCopy bold color="primary">
                {resource.title}
              </BodyCopy>
              <div className="flex gap-sm flex-wrap">
                <PinToggle
                  color="gray-500"
                  goal={task.goal}
                  journeyId={journey.id}
                  resourceId={resource.id}
                  resourceType={PinResourceTypeEnum.RESOURCE}
                />
                {myExpert && (
                  <>
                    <CircleButton
                      ariaLabelKey="accessibility.ariaLabels.edit"
                      fill="clear"
                      icon="edit"
                      onClick={generateHandleEdit(resource)}
                      size="medium"
                    />
                    <CircleButton
                      ariaLabelKey="accessibility.ariaLabels.delete"
                      fill="clear"
                      icon="trash"
                      onClick={generateHandleDelete(resource)}
                      size="medium"
                    />
                    <IonReorder slot="start" />
                  </>
                )}
              </div>
            </div>

            {resource.cost && (
              <div className="flex margin-10-bottom">
                <div className="ion-margin-end">
                  <Icon color="gray-200" icon="moneyBill" />
                </div>
                <BodyCopy color="gray" size="small">
                  {resource.cost}
                </BodyCopy>
              </div>
            )}
            {resource.phoneNumber && (
              <div className="flex margin-10-bottom">
                <div className="ion-margin-end margin-4-top">
                  <Icon color="gray-200" icon="phone" />
                </div>

                <ExternalLink
                  href={`tel:${resource.phoneNumber}`}
                  linkEventCreateVariables={{
                    journeyId: journey.id,
                    resourceId: resource.id,
                    resourceType: resource.__typename
                  }}
                >
                  {formatPhoneNumber(resource.phoneNumber)
                    ? formatPhoneNumber(resource.phoneNumber)
                    : resource.phoneNumber}
                </ExternalLink>
              </div>
            )}

            {resource.linkUrl && (
              <div className="flex margin-10-bottom">
                <div className="ion-margin-end margin-4-top">
                  <Icon color="gray-200" icon="globe" />
                </div>
                <ExternalLink
                  href={resource.linkUrl}
                  linkEventCreateVariables={{
                    journeyId: journey.id,
                    resourceId: resource.id,
                    resourceType: resource.__typename
                  }}
                >
                  {resource.linkTitle}
                </ExternalLink>
              </div>
            )}

            <div className="margin-20-top">
              <ResourceFeatures journeyId={journey.id} resource={resource} />
            </div>

            <div className="margin-20-top">
              <BodyCopy color="gray">{sanitizedDescription}</BodyCopy>
            </div>
          </div>
          <div className="flex justify-around">
            <Button fill="clear" onClick={() => updateUseful(resource, false)}>
              <IconBase color="primary" icon="xmark" slot="start" />
              <BodyCopy bold color="primary" size="small">
                <FormattedMessage id="components.CarePlan.clientVisibleTask.notUseful" />
              </BodyCopy>
            </Button>
            <Button
              color={resource.useful ? "success" : "primary-300"}
              onClick={() => updateUseful(resource, true)}
              size="large"
            >
              <BodyCopy color="white">
                {resource.useful ? (
                  <FormattedMessage id="components.CarePlan.clientVisibleTask.selected" />
                ) : (
                  <FormattedMessage id="components.CarePlan.clientVisibleTask.useful" />
                )}
              </BodyCopy>
              {resource.useful && <IconBase color="white" icon="checkmark" slot="end" />}
            </Button>
          </div>
        </div>
      }
      className={styles.resourceCard}
    />
  );
};

export default ClientVisibleTaskResourceCard;
